import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, Legend } from "recharts";
import { Loader2 } from "lucide-react";
import { format, subMonths, startOfMonth, endOfMonth } from "date-fns";

interface ChartData {
  name: string;
  value: number;
}

interface TimeSeriesData {
  month: string;
  claims?: number;
  placements?: number;
  users?: number;
}

const COLORS = ['hsl(var(--primary))', 'hsl(var(--secondary))', 'hsl(var(--accent))', '#8884d8', '#82ca9d', '#ffc658'];

export function ReportCharts() {
  const [loading, setLoading] = useState(true);
  const [claimsOverTime, setClaimsOverTime] = useState<TimeSeriesData[]>([]);
  const [claimsByStatus, setClaimsByStatus] = useState<ChartData[]>([]);
  const [placementsByStatus, setPlacementsByStatus] = useState<ChartData[]>([]);
  const [usersByRole, setUsersByRole] = useState<ChartData[]>([]);

  useEffect(() => {
    fetchChartData();
  }, []);

  const fetchChartData = async () => {
    setLoading(true);
    try {
      // Fetch claims by month (last 6 months)
      const months: TimeSeriesData[] = [];
      for (let i = 5; i >= 0; i--) {
        const monthDate = subMonths(new Date(), i);
        const monthStart = startOfMonth(monthDate);
        const monthEnd = endOfMonth(monthDate);
        const monthLabel = format(monthDate, 'MMM yyyy');

        // Claims count
        const { count: claimsCount } = await supabase
          .from('claims')
          .select('*', { count: 'exact', head: true })
          .gte('created_at', monthStart.toISOString())
          .lte('created_at', monthEnd.toISOString());

        // Placements count
        const { count: placementsCount } = await supabase
          .from('placements')
          .select('*', { count: 'exact', head: true })
          .gte('created_at', monthStart.toISOString())
          .lte('created_at', monthEnd.toISOString());

        // Users count
        const { count: usersCount } = await supabase
          .from('profiles')
          .select('*', { count: 'exact', head: true })
          .gte('created_at', monthStart.toISOString())
          .lte('created_at', monthEnd.toISOString());

        months.push({
          month: monthLabel,
          claims: claimsCount || 0,
          placements: placementsCount || 0,
          users: usersCount || 0,
        });
      }
      setClaimsOverTime(months);

      // Fetch claims by status
      const { data: claimsData } = await supabase
        .from('claims')
        .select('status');
      
      if (claimsData) {
        const statusCounts: Record<string, number> = {};
        claimsData.forEach((claim) => {
          statusCounts[claim.status] = (statusCounts[claim.status] || 0) + 1;
        });
        setClaimsByStatus(
          Object.entries(statusCounts).map(([name, value]) => ({ name, value }))
        );
      }

      // Fetch placements by status
      const { data: placementsData } = await supabase
        .from('placements')
        .select('status');
      
      if (placementsData) {
        const statusCounts: Record<string, number> = {};
        placementsData.forEach((placement) => {
          statusCounts[placement.status] = (statusCounts[placement.status] || 0) + 1;
        });
        setPlacementsByStatus(
          Object.entries(statusCounts).map(([name, value]) => ({ name, value }))
        );
      }

      // Fetch users by role
      const { data: rolesData } = await supabase
        .from('user_roles')
        .select('role');
      
      if (rolesData) {
        const roleCounts: Record<string, number> = {};
        rolesData.forEach((role) => {
          roleCounts[role.role] = (roleCounts[role.role] || 0) + 1;
        });
        setUsersByRole(
          Object.entries(roleCounts).map(([name, value]) => ({ name, value }))
        );
      }
    } catch (error) {
      console.error('Error fetching chart data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Activity Over Time */}
      <Card>
        <CardHeader>
          <CardTitle>Activity Trends (Last 6 Months)</CardTitle>
          <CardDescription>Claims, placements, and new users over time</CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={claimsOverTime}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis dataKey="month" className="text-xs" />
              <YAxis className="text-xs" />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'hsl(var(--background))', 
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '8px'
                }} 
              />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="claims" 
                stroke="hsl(var(--primary))" 
                strokeWidth={2}
                name="Claims"
              />
              <Line 
                type="monotone" 
                dataKey="placements" 
                stroke="hsl(var(--secondary))" 
                strokeWidth={2}
                name="Placements"
              />
              <Line 
                type="monotone" 
                dataKey="users" 
                stroke="hsl(var(--accent))" 
                strokeWidth={2}
                name="New Users"
              />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {/* Claims by Status */}
        {claimsByStatus.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Claims by Status</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={claimsByStatus}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={70}
                    paddingAngle={5}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {claimsByStatus.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Placements by Status */}
        {placementsByStatus.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Placements by Status</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={placementsByStatus}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="name" className="text-xs" />
                  <YAxis className="text-xs" />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--background))', 
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }} 
                  />
                  <Bar dataKey="value" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Users by Role */}
        {usersByRole.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Users by Role</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={usersByRole}
                    cx="50%"
                    cy="50%"
                    outerRadius={70}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value}`}
                  >
                    {usersByRole.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}